import client from '../client';
import type { StoreSettings, DomainSearchResult, DnsRecord, DnsRecordType } from '../../types/settings';
import type { ApiResponse } from '../../types/api';
import type { AutomationRule } from '../../types/order';

export const settingsApi = {
  get: async (storeId: string) => {
    const res = await client.get<ApiResponse<{ settings: StoreSettings }>>(
      `/dashboard/${storeId}/settings`,
    );
    return res.data.data!;
  },

  updateGeneral: async (storeId: string, data: Record<string, unknown>) => {
    await client.patch(`/dashboard/${storeId}/settings/general`, data);
  },

  updateSeo: async (storeId: string, data: Record<string, unknown>) => {
    await client.patch(`/dashboard/${storeId}/settings/seo`, data);
  },

  updateNotifications: async (storeId: string, data: Record<string, unknown>) => {
    await client.patch(`/dashboard/${storeId}/settings/notifications`, data);
  },

  updatePaymentMethods: async (storeId: string, data: Record<string, unknown>) => {
    await client.patch(`/dashboard/${storeId}/settings/payment-methods`, data);
  },

  updateClientAuth: async (storeId: string, data: Record<string, unknown>) => {
    await client.patch(`/dashboard/${storeId}/settings/client-auth`, data);
  },

  searchDomain: async (storeId: string, domain: string) => {
    const res = await client.post<ApiResponse<DomainSearchResult>>(
      `/dashboard/${storeId}/settings/domain/search`,
      { domain },
    );
    return res.data.data!;
  },

  updateDomain: async (storeId: string, data: { domain?: string }) => {
    await client.patch(`/dashboard/${storeId}/settings/domain`, data);
  },

  updateStore: async (storeId: string, data: Record<string, unknown>) => {
    await client.patch(`/dashboard/${storeId}`, data);
  },

  updateTheme: async (storeId: string, data: Record<string, unknown>) => {
    await client.patch(`/dashboard/${storeId}/settings/theme`, data);
  },

  updateHeaderSettings: async (storeId: string, data: Record<string, unknown>) => {
    await client.patch(`/dashboard/${storeId}/settings/header-settings`, data);
  },

  updateWebhooks: async (storeId: string, data: Record<string, unknown>) => {
    // Returns the signing secret, which the backend mints on the first save
    // that actually configures a webhook.
    const res = await client.patch<ApiResponse<{ webhookSecret: string | null }>>(
      `/dashboard/${storeId}/settings/webhooks`,
      data,
    );
    return res.data.data ?? { webhookSecret: null };
  },

  rotateWebhookSecret: async (storeId: string) => {
    const res = await client.post<ApiResponse<{ webhookSecret: string }>>(
      `/dashboard/${storeId}/settings/webhooks/secret`,
    );
    return res.data.data!.webhookSecret;
  },

  testWebhook: async (storeId: string, payload: { url: string; event: string }) => {
    const res = await client.post<ApiResponse<{ delivered: boolean; status: number; statusText: string; elapsedMs: number }>>(
      `/dashboard/${storeId}/settings/webhooks/test`,
      payload,
    );
    return res.data.data!;
  },

  updateTracking: async (storeId: string, data: Record<string, unknown>) => {
    await client.patch(`/dashboard/${storeId}/settings/tracking`, data);
  },

  getAutomation: async (storeId: string) => {
    const res = await client.get<ApiResponse<{ automation: { rules: AutomationRule[]; enabled: boolean } }>>(
      `/dashboard/${storeId}/settings/automation`,
    );
    return res.data.data!;
  },

  updateAutomation: async (storeId: string, data: Record<string, unknown>) => {
    await client.patch(`/dashboard/${storeId}/settings/automation`, data);
  },

  addLanguage: async (storeId: string, language: string) => {
    await client.post(`/dashboard/${storeId}/settings/languages/add`, { language });
  },

  removeLanguage: async (storeId: string, language: string) => {
    await client.post(`/dashboard/${storeId}/settings/languages/remove`, { language });
  },

  setDefaultLanguage: async (storeId: string, language: string) => {
    await client.post(`/dashboard/${storeId}/settings/languages/setDefault`, { language });
  },

  getSupportedLanguages: async (storeId: string) => {
    const res = await client.get<ApiResponse<{ languages: Array<{ code: string; english: string; native: string }> }>>(
      `/dashboard/${storeId}/settings/languages/supported`,
    );
    return res.data.data!.languages;
  },

  registerDomain: async (storeId: string, domain: string) => {
    await client.post(`/dashboard/${storeId}/settings/domain`, { domain });
  },

  removeDomain: async (storeId: string) => {
    await client.delete(`/dashboard/${storeId}/settings/domain`);
  },

  listDnsRecords: async (storeId: string) => {
    const res = await client.get<ApiResponse<{ domain: string | null; records: DnsRecord[] }>>(
      `/dashboard/${storeId}/settings/dns-records`,
    );
    return res.data.data!;
  },

  addDnsRecord: async (
    storeId: string,
    data: { name: string; type: DnsRecordType; value: string },
  ) => {
    const res = await client.post<ApiResponse<{ record: DnsRecord }>>(
      `/dashboard/${storeId}/settings/dns-records`,
      data,
    );
    return res.data.data!.record;
  },

  removeDnsRecord: async (storeId: string, recordId: string) => {
    await client.delete(`/dashboard/${storeId}/settings/dns-records/${recordId}`);
  },

  updateEmailBranding: async (
    storeId: string,
    data: { brandColor?: string; buttonColor?: string; backgroundColor?: string },
  ) => {
    const res = await client.patch<ApiResponse<{ emailBranding: Record<string, string> }>>(
      `/dashboard/${storeId}/settings/email-branding`,
      data,
    );
    return res.data.data!;
  },

  sendEmailBrandingTest: async (storeId: string, to?: string) => {
    const res = await client.post<ApiResponse<{ sent: boolean; to: string }>>(
      `/dashboard/${storeId}/settings/email-branding/test-send`,
      to ? { to } : {},
    );
    return res.data.data!;
  },
};
